import { useEffect, useState } from 'react';
import { proposalApi, Proposal } from '../api/proposalApi';
import { BrandingData } from '../api/brandingApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import ProposalStatusBadge from './ProposalStatusBadge';
import Spinner from './Spinner';
import { ExternalLink, X, FileText } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  proposalId?: string;
  branding?: BrandingData | null;
}

const cleanMarkdown = (text: string) => {
  if (!text) return '';
  return text
    .replace(/__##/g, '##')
    .replace(/##(\d)/g, '## $1')
    .replace(/([^\n])\n(#{1,2}\s+)/g, '$1\n\n$2')
    .replace(/\n---/g, '\n\n---\n\n');
};

export default function ProposalView({ isOpen, onClose, proposalId, branding }: Props) {
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && proposalId) {
      const fetchProposal = async () => {
        setLoading(true);
        try {
          const data = await proposalApi.getProposalById(proposalId);
          setProposal(data);
        } catch (err) {
          setProposal(null);
        } finally {
          setLoading(false);
        }
      };
      fetchProposal();
    }
  }, [isOpen, proposalId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-slate-50 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col animate-scale-in">
        {/* Modal Header */}
        <div className="bg-white px-8 py-6 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600">
              <FileText size={20} />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 tracking-tight">Proposal Preview</h2>
              {proposal && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{proposal.title}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {proposal && (
              <button 
                onClick={() => window.open(`/proposal/${proposal.publicToken}`, '_blank')}
                className="p-2 text-slate-400 hover:text-blue-600 transition-colors"
                title="View Public Link"
              >
                <ExternalLink size={20} />
              </button>
            )}
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 sm:p-12">
          {loading ? (
            <div className="flex justify-center py-20"><Spinner /></div>
          ) : proposal ? (
            <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden min-h-full">
              <div className="p-8 sm:p-12 border-b border-slate-50 bg-slate-50/30">
                <div className="flex flex-col sm:flex-row justify-between gap-6">
                  <div className="space-y-1 text-left">
                    <div className="flex items-center gap-3">
                      <h1 className="text-3xl font-black text-slate-900 tracking-tight">{proposal.title}</h1>
                      <ProposalStatusBadge status={proposal.status} />
                    </div>
                    <p className="text-slate-500 font-medium">Prepared for <span className="text-slate-900 font-bold">{proposal.customerName}</span></p>
                  </div>
                  <div className="text-right"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Expiry Date</label><div className="font-bold text-slate-900">{new Date(proposal.expiryDate).toLocaleDateString()}</div></div>
                </div>
              </div>

              <div 
                className="
                  p-8 sm:p-16 prose max-w-none text-left bg-white
                  prose-headings:!text-slate-900
                  prose-h1:!text-slate-900
                  prose-h2:!text-slate-900
                  prose-p:text-slate-600
                  prose-li:text-slate-600
                  prose-strong:text-slate-900
                  prose-headings:font-black
                  prose-headings:tracking-tight
                  prose-ul:list-disc prose-ul:pl-5
                  prose-ol:list-decimal prose-ol:pl-5
                "
              >
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={{
                    h1: ({ children, ...props }) => <h1 style={{ color: "#0f172a" }} {...props}>{children}</h1>,
                    h2: ({ children, ...props }) => <h2 style={{ color: "#0f172a" }} {...props}>{children}</h2>,
                    p: ({ ...props }) => <p style={{ color: "#334155" }} className="mb-4 leading-relaxed" {...props} />,
                    blockquote: ({ ...props }) => (
                      <blockquote 
                        style={{ 
                          borderLeftColor: "#6366f1", 
                          backgroundColor: "#f8fafc", 
                          color: "#475569" 
                        }} 
                        className="border-l-4 p-4 my-6 italic rounded-r-xl" 
                        {...props} 
                      />
                    ),
                    ul: ({ ...props }) => <ul style={{ color: "#334155" }} className="list-disc pl-5 my-2 space-y-1" {...props} />,
                    ol: ({ ...props }) => <ol style={{ color: "#334155" }} className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                    li: ({ ...props }) => <li style={{ color: "#334155" }} className="pl-1" {...props} />,
                    hr: ({ ...props }) => <hr className="my-8 border-t-2 border-slate-200" style={{ borderColor: "#e2e8f0" }} {...props} />,
                    table: ({ ...props }) => <table className="w-full border-collapse border border-slate-300 my-6 text-sm" {...props} />,
                    thead: ({ ...props }) => <thead className="bg-slate-100" {...props} />,
                    th: ({ ...props }) => <th className="border border-slate-300 px-4 py-2 text-left font-bold" style={{ color: "#0f172a" }} {...props} />,
                    td: ({ ...props }) => <td className="border border-slate-300 px-4 py-2 text-slate-600" {...props} />
                  }}
                >
                  {cleanMarkdown(proposal.content)}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-slate-400 font-bold uppercase tracking-widest">Proposal Not Found</div>
          )}
        </div>
      </div>
    </div>
  );
}